import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, Save, ClipboardList, AlertCircle } from "lucide-react";

// Roles allowed to edit the final summary (all except read-only 'consulta').
const EDITOR_ROLES = new Set([
  "super_admin",
  "dueno",
  "gerente",
  "recepcion",
  "tecnico",
  "almacen",
  "caja",
]);

// The final summary can only be captured once the vehicle is ready or delivered.
const EDITABLE_STATUSES = new Set(["listo_para_entrega", "entregado"]);

const EMPTY = {
  work_done: "",
  observations: "",
  recommendations: "",
  next_service_note: "",
};

export function FinalSummaryPanel({ serviceOrder, onChanged }) {
  const { session } = useAuth();
  const roleName = session?.user?.role_name;
  const canEditRole = EDITOR_ROLES.has(roleName);
  const statusAllowsEdit = EDITABLE_STATUSES.has(serviceOrder?.status);
  const canEdit = canEditRole && statusAllowsEdit;

  const existing = serviceOrder?.final_summary || null;

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm({
      work_done: existing?.work_done || "",
      observations: existing?.observations || "",
      recommendations: existing?.recommendations || "",
      next_service_note: existing?.next_service_note || "",
    });
    setDirty(false);
  }, [existing?.updated_at, serviceOrder?.id]);

  function upd(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      await api.patch(`/service-orders/${serviceOrder.id}/final-summary`, {
        work_done: form.work_done || null,
        observations: form.observations || null,
        recommendations: form.recommendations || null,
        next_service_note: form.next_service_note || null,
      });
      toast.success("Resumen final guardado");
      setDirty(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  const isEmpty = !form.work_done && !form.observations && !form.recommendations && !form.next_service_note;

  return (
    <Card className="border-border" data-testid="final-summary-panel">
      <CardHeader className="border-b border-border pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardList className="h-4 w-4" />
          Resumen final para el cliente
          {existing?.updated_at && (
            <span className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-emerald-700">
              <CheckCircle2 className="h-3 w-3" />
              Registrado
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {!statusAllowsEdit && (
          <Alert className="bg-amber-50 border-amber-200" data-testid="final-summary-status-lock">
            <AlertCircle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-sm text-amber-900">
              El resumen final se habilita cuando la orden pasa a <strong>listo para entrega</strong> o <strong>entregado</strong>.
            </AlertDescription>
          </Alert>
        )}

        {statusAllowsEdit && !canEditRole && (
          <Alert className="bg-slate-50 border-slate-200">
            <AlertCircle className="h-4 w-4 text-slate-600" />
            <AlertDescription className="text-sm text-slate-700">
              Tu rol es solo de consulta. Puedes leer el resumen pero no editarlo.
            </AlertDescription>
          </Alert>
        )}

        <p className="text-xs text-muted-foreground">
          Lo que captures aquí será visible para el cliente en su portal público
          (enlace/QR). Sé claro y breve — describe qué se hizo, qué se observó y qué recomiendas.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label htmlFor="fs-work-done" className="text-xs uppercase tracking-wider text-muted-foreground">
              Trabajo realizado
            </Label>
            <Textarea
              id="fs-work-done"
              data-testid="fs-work-done"
              rows={4}
              placeholder="Ej. Cambio de aceite y filtro, revisión de frenos, alineación."
              value={form.work_done}
              onChange={(e) => upd("work_done", e.target.value)}
              disabled={!canEdit || saving}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="fs-observations" className="text-xs uppercase tracking-wider text-muted-foreground">
              Observaciones
            </Label>
            <Textarea
              id="fs-observations"
              data-testid="fs-observations"
              rows={4}
              placeholder="Ej. Se detectó fuga leve en junta de cárter, no requiere acción inmediata."
              value={form.observations}
              onChange={(e) => upd("observations", e.target.value)}
              disabled={!canEdit || saving}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="fs-recommendations" className="text-xs uppercase tracking-wider text-muted-foreground">
              Recomendaciones
            </Label>
            <Textarea
              id="fs-recommendations"
              data-testid="fs-recommendations"
              rows={4}
              placeholder="Ej. Reemplazar balatas delanteras en los próximos 2,000 km."
              value={form.recommendations}
              onChange={(e) => upd("recommendations", e.target.value)}
              disabled={!canEdit || saving}
              className="mt-1"
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="fs-next-service" className="text-xs uppercase tracking-wider text-muted-foreground">
              Próximo servicio sugerido
            </Label>
            <Textarea
              id="fs-next-service"
              data-testid="fs-next-service"
              rows={2}
              placeholder="Ej. Servicio mayor al alcanzar 60,000 km o en 6 meses."
              value={form.next_service_note}
              onChange={(e) => upd("next_service_note", e.target.value)}
              disabled={!canEdit || saving}
              className="mt-1"
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="text-xs text-muted-foreground">
            {existing?.updated_at
              ? `Última actualización: ${new Date(existing.updated_at).toLocaleString("es-MX")}`
              : isEmpty
                ? "Sin resumen capturado todavía."
                : "Cambios sin guardar."}
          </div>
          <Button
            size="sm"
            className="btn-tenant"
            onClick={save}
            disabled={!canEdit || saving || !dirty}
            data-testid="fs-save-btn"
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Guardando…" : "Guardar resumen"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
